import React from 'react';
import { Device } from '../../lib/types';
import type { SortDir } from '../../lib/types';
import { formatDaysAgo } from '../../lib/utils';
import Button from '../../components/Button/Button';
import { SkeletonRows } from './SkeletonRow';
import Pagination from '../../components/Pagination/Pagination';
import SortableHeader from './SortableHeader';
import CollapsibleSection from './CollapsibleSection';
import PetNameCell from './PetNameCell';
import styles from './Devices.module.scss';

/** Columns the stalled table can be sorted by (clicking their header). */
export type StalledSortKey = 'adopter' | 'pet' | 'stalled';

const columns: { label: string; sortKey?: StalledSortKey }[] = [
  { label: 'Adopter', sortKey: 'adopter' },
  { label: 'Pet', sortKey: 'pet' },
  { label: 'Device' },
  { label: 'Stalled', sortKey: 'stalled' },
  { label: 'Action' },
];

interface StalledTransfersTableProps {
  devices: Device[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  /** Age threshold (in days) that qualifies a pending transfer as stalled. */
  thresholdDays: number;
  onPageChange: (page: number) => void;
  skeletonCount: number;
  sortKey: StalledSortKey;
  sortDir: SortDir;
  onSort: (key: StalledSortKey) => void;
  onMarkRecovered: (device: Device) => void;
  onNotRecovered: (device: Device) => void;
  onSendSms: (device: Device) => void;
  onCancelTransfer: (device: Device) => void;
}

export default function StalledTransfersTable({
  devices,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  thresholdDays,
  onPageChange,
  skeletonCount,
  sortKey,
  sortDir,
  onSort,
  onMarkRecovered,
  onNotRecovered,
  onSendSms,
  onCancelTransfer,
}: StalledTransfersTableProps) {
  // Hide the section entirely when there are no stalled transfers and we're not
  // showing first-load skeletons. Keying off `loading` here would render an
  // empty "Stalled Transfers (0)" for the whole multi-page pending drain (which
  // keeps `loading` true); `skeletonCount` already covers the initial load.
  if (totalCount === 0 && skeletonCount === 0) {
    return null;
  }

  const hasData = devices.length > 0;
  const showOverlay = skeletonCount > 0 && hasData;
  const showSkeleton = skeletonCount > 0 && !hasData;
  const wrapperClass = `${styles.tableWrapper}${showOverlay ? ` ${styles.tableTransitioning}` : ''}`;

  return (
    <CollapsibleSection
      title="Stalled"
      count={totalCount}
      variant="warning"
      description={`Adoption transfers still awaiting activation after ${thresholdDays} days. Reach out to the adopter, or recover the device if the adoption fell through.`}
    >
      <div className={wrapperClass}>
        <table className={styles.table}>
          <colgroup>
            <col style={{ width: '18%' }} /> {/* Adopter */}
            <col style={{ width: '13%' }} /> {/* Pet */}
            <col style={{ width: '16%' }} /> {/* Device */}
            <col style={{ width: '13%' }} /> {/* Stalled */}
            <col style={{ width: '40%' }} /> {/* Action */}
          </colgroup>
          <thead>
            <tr>
              {columns.map((col) => {
                const key = col.sortKey;
                if (!key) {
                  return <th key={col.label}>{col.label}</th>;
                }
                return (
                  <SortableHeader
                    key={col.label}
                    label={col.label}
                    active={sortKey === key}
                    dir={sortDir}
                    onClick={() => onSort(key)}
                  />
                );
              })}
            </tr>
          </thead>
          <tbody>
            {showSkeleton ? (
              <SkeletonRows count={skeletonCount} columns={columns.length} />
            ) : (
              devices.map((d) => (
                <tr key={d.id} className={styles.stalledRow}>
                  <td>
                    {d.adopterName}
                    <br />
                    <span className={styles.adopterEmail}>{d.adopterEmail}</span>
                  </td>
                  <td>{d.petName ? <PetNameCell name={d.petName} photoThumbUrl={d.petPhotoThumbUrl} /> : '—'}</td>
                  <td>
                    <div className={styles.deviceId}>{d.id}</div>
                    <div className={styles.deviceType}>{d.type}</div>
                  </td>
                  <td>
                    <span className={styles.stalledAge}>{formatDaysAgo(d.transferDate)}</span>
                  </td>
                  <td>
                    <div className={styles.actionGroup}>
                      <Button variant="greenLight" size="sm" onClick={() => onMarkRecovered(d)}>
                        Mark Recovered
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => onSendSms(d)}>
                        Send SMS
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => onNotRecovered(d)}>
                        Not Recovered
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => onCancelTransfer(d)}>
                        Cancel
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
